import React, { useContext, useRef, useState } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import StepWizard from 'react-step-wizard'
import DialogContext from '../../Authentication/DialogContext'
import FormFields, { FormFieldsProps } from '../../components/FormFields'
import TlsService from '../../service/TlsService'
import { hideDialog } from '../../utils/DialogUtil'

const KeyStoreImport = (props: { setKeys: any }) => {
  const [keystore, setKeystore] = useState({} as any)

  const transitions: any = {
    enterRight: 'animated enterRight',
    enterLeft: 'animated enterLeft',
    exitRight: 'animated exitRight',
    exitLeft: 'animated exitLeft',
    intro: 'animated intro',
  }

  return (
    <StepWizard transitions={transitions} isLazyMount>
      <UploadKeystore setKeystore={setKeystore} />
      <SelectAlias keystore={keystore} setKeys={props.setKeys} />
    </StepWizard>
  )
}

const UploadKeystore = (props: any) => {
  const { confirmDialogPropsSetter } = useContext(DialogContext)
  const [submitting, setSubmitting] = useState(false)
  const { t } = useTranslation()

  const forwardedRef = useRef<HTMLButtonElement>(null)

  const submit = () => {
    if (forwardedRef != null && forwardedRef.current != null) {
      forwardedRef.current.click()
    }
  }

  const handleResponseCustom = (keyStoreInfo: any) => {
    props.setKeystore(keyStoreInfo)
    props.goToStep(2)
  }

  const ffProps: FormFieldsProps = {
    setSubmitting,
    forwardedRef,
    onSubmit: TlsService.getKeystoreAliases,
    handleResponseCustom,
    data: [
      {
        id: 'keystore',
        type: 'file',
        label: t('keys.keystore'),
        required: true,
        requiredMessage: t('invalidMessage', { label: 'keystore' }),
      },
      {
        id: 'keyStorePassword',
        type: 'password',
        autoComplete: 'keyStore-password',
        label: t('keys.keyStorePassword'),
        required: true,
        requiredMessage: t('invalidMessage', { label: 'keystore password' }),
      },
    ],
    labelGridSize: 4,
  }

  return (
    <>
      <FormFields {...ffProps} />
      <Modal.Footer>
        <Button onClick={submit} disabled={submitting} block={true} className='w-auto'>
          {submitting ? (
            <>
              <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' />
              <span className='ml-1'>{t('submitting')}</span>
            </>
          ) : (
            'Next'
          )}
        </Button>
        <Button variant='outline-danger' onClick={() => hideDialog(confirmDialogPropsSetter)}>
          Cancel
        </Button>
      </Modal.Footer>
    </>
  )
}

const SelectAlias = (props: any) => {
  const { confirmDialogPropsSetter } = useContext(DialogContext)
  const [submitting, setSubmitting] = useState(false)
  const { t } = useTranslation()

  const forwardedRef = useRef<HTMLButtonElement>(null)

  const submit = () => {
    if (forwardedRef != null && forwardedRef.current != null) {
      forwardedRef.current.click()
    }
  }

  const keystoreAliases = props.keystore.keystoreAliases
  const aliases = Object.keys(keystoreAliases)
  const initialAlias: string = aliases.length > 0 ? '' + aliases[0] : ''

  const ffProps: FormFieldsProps = {
    setSubmitting,
    forwardedRef,
    onSubmit: TlsService.importKeystore,
    onSubmitExtraArgs: [props.keystore, props.setKeys, t],
    data: (custom: string[]) => [
      {
        id: 'alias',
        type: 'select',
        options: aliases,
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        dependentUpdate: ['name'],
        value: custom[0],
        label: t('keys.keyAlias'),
        required: true,
        requiredMessage: t('invalidMessage', { label: 'alias' }),
      },
      {
        id: 'keyPassword',
        type: 'password',
        autoComplete: 'key-password',
        label: t('keys.keyPassword'),
        render: keystoreAliases[custom[0]] === 'DIFFERENT_PASSWORD',
        required: keystoreAliases[custom[0]] === 'DIFFERENT_PASSWORD',
        requiredMessage: t('invalidMessage', { label: 'Key Password' }),
      },
      {
        id: 'name',
        type: 'text',
        label: t('name.text'),
        value: custom[0],
        required: true,
        requiredMessage: t('invalidMessage', { label: 'name' }),
      },
    ],
    initCustom: [initialAlias],
    labelGridSize: 4,
  }

  return (
    <>
      <FormFields {...ffProps} />
      <Modal.Footer>
        <Button onClick={submit} disabled={submitting} block={true} className='w-auto'>
          {submitting ? (
            <>
              <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' />
              <span className='ml-1'>{t('submitting')}</span>
            </>
          ) : (
            'Submit'
          )}
        </Button>
        <Button variant='outline-danger' onClick={() => hideDialog(confirmDialogPropsSetter)}>
          Cancel
        </Button>
      </Modal.Footer>
    </>
  )
}

export default KeyStoreImport
